import HotelAddress from './HotelAddress'
import Image from './Image'
import HotelNotificationMessage from './HotelNotificationMessage'
import IntervalTimeLabel from './IntervalTimeLabel'
import Coordinate from '@/domain/Coordinate'

export default class Hotel {
  static fromJSON (json) {
    return new Hotel({
      code: json.code,
      tmsCode: json.tmsCode,
      brandCode: json.brand,
      theatre: json.theatre,
      coBrandCode: json.coBrand ? json.coBrand : null,
      name: json.name,
      address: HotelAddress.fromJSON(json.address || {}),
      coordinates: Coordinate.fromJSON(json.coordinates || {}),
      phone: json.phone,
      region: json.region,
      link: json.overviewPath,
      contactURL: json.contactPath,
      notificationMessages: (json.notificationMessages || [])
        .map(it => HotelNotificationMessage.fromJSON(it))
        .filter(it => it.toBeShown()),
      defaultImage: Image.fromJSON(json.image || {}),
      percentageDiscount: 'TODO, pending backend',
      exchangeRates: json.exchangeRates,
      rooms: json.rooms || [],
      currency: json.currency,
      tierDiscount: json.tierDiscount,
      matchesSearch: json.matchesSearch,
      minNightsPerStay: json.minNightsPerStay,
      jsonForIntegrateWithGallery: json,
      newlyRenovated: IntervalTimeLabel.fromJSON(json.newlyRenovated || {}).toBeShown(),
      showNonRefundableRatesInfo: json.showNonRefundableRatesInfo ? json.showNonRefundableRatesInfo : false,
      nonRefundableRatesInfo: json.nonRefundableRatesInfo,
      globalSeo: json.globalSeo,
      subBrand: json.subBrand
    })
  }

  constructor({
    code, tmsCode, brandCode, theatre, coBrandCode, name,
    address, coordinates, phone, region, link, contactURL,
    notificationMessages, defaultImage, percentageDiscount,
    currency, tierDiscount, matchesSearch, minNightsPerStay,
    rooms, jsonForIntegrateWithGallery, newlyRenovated, showNonRefundableRatesInfo,
    nonRefundableRatesInfo, globalSeo, subBrand
  }) {
    this.code = code
    this.brandCode = brandCode
    this.theatre = theatre
    this.coBrandCode = coBrandCode
    this.tmsCode = tmsCode
    this.name = name
    this.address = address
    this.coordinates = coordinates
    this.phone = phone
    this.region = region
    this.showNonRefundableRatesInfo = showNonRefundableRatesInfo
    this.link = link
    this.contactURL = contactURL
    this.notificationMessages = notificationMessages
    this.defaultImage = defaultImage
    this.percentageDiscount = percentageDiscount
    this.rooms = rooms || []
    this.currency = currency
    this.tierDiscount = tierDiscount
    this.matchesSearch = matchesSearch
    this.maxDiscount = this.rooms.reduce((acc, room) => room.maxDiscount > acc ? room.maxDiscount : acc, 0)
    this.isPayUponArrival = this.rooms.filter(room => room.isPayUponArrival).length > 0
    this.minNightsPerStay = minNightsPerStay || 0
    this.jsonForIntegrateWithGallery = jsonForIntegrateWithGallery
    this.newlyRenovated = newlyRenovated
    this.nonRefundableRatesInfo = nonRefundableRatesInfo
    this.globalSeo = globalSeo
    this.subBrand = subBrand
  }

  applyFilter (filter, isLogged) {
    const rooms = this.rooms
      .map(room => room.applyFilter(filter, isLogged))
      .filter(room => room.hasAvailableStays())
      .sort((a, b) => a.cheaperStay().weight() - b.cheaperStay().weight())

    const matches = rooms
      .reduce((accumulation, room) => {
        const matches = room.count(filter)
        Object.keys(matches)
          .forEach((key) => {
            accumulation[key] = (accumulation[key] || 0) + matches[key]
          })
        return accumulation
      }, {})

    return {
      filteredHotel: new Hotel({ ...this, rooms }),
      recalculatedFilter: filter.recalculateCounters(matches)
    }
  }

  changeCurrency (selectedCurrency) {
    const rooms = this.rooms.map(room => room.changeCurrency(selectedCurrency))
    return new Hotel({ ...this, rooms: rooms })
  }

  hasAvailableStays () {
    return this.rooms
      .filter(room => room.hasAvailableStays())
      .length > 0
  }

  hasGallery () {
    return this.jsonForIntegrateWithGallery && this.jsonForIntegrateWithGallery.galleries && this.jsonForIntegrateWithGallery.galleries.length > 0
  }

  firstSubGalleryId () {
    return this.hasGallery() && this.jsonForIntegrateWithGallery.galleries[0].category.toLowerCase()
  }

  jsonHotel () {
    return this.jsonForIntegrateWithGallery
  }

  getOnlyCashRooms () {
    if (!this.rooms) return []
    return this.rooms.filter(room => !!room.getOnlyCashStays().length)
  }

  hasOnlyCashRooms () { return !!this.getOnlyCashRooms().length }

  getOnlyPointsRooms (customerPoints) {
    if (!this.rooms) return []
    return this.rooms.filter(room => !!room.getOnlyPointsStays(customerPoints).length)
  }

  hasOnlyPointsRooms (customerPoints) { return !!this.getOnlyPointsRooms(customerPoints).length }

  getCashAndPointsRooms (customerPoints) {
    if (!this.rooms) return []
    return this.rooms.filter(room => !!room.getCashAndPointsStays(customerPoints).length)
  }

  hasCashAndPointsRooms (customerPoints) { return !!this.getCashAndPointsRooms(customerPoints).length }
}
